import type { RegistryArtifact } from '../services/artifactRegistry';
import type { StageStatus } from './displayStrategy';
import type { WorkspaceArtifact } from './workspaceViewModel';

/**
 * A registry artifact together with every version derived from it.
 *
 * The backend stores each repair/patch as its own row pointing at its parent
 * via `parentArtifactId`, so a "version history" is a chain rooted at the
 * artifact that has no parent inside the result set.
 */
export interface ArtifactVersionChain {
  /** Root artifact of the chain (lowest version). */
  root: RegistryArtifact;
  /** Every version oldest → newest, including the root. */
  versions: RegistryArtifact[];
  /** Newest version — what the workspace shows by default. */
  latest: RegistryArtifact;
  active?: RegistryArtifact;
}

const KIND_BY_TYPE: Record<string, WorkspaceArtifact['kind']> = {
  'generated-code': 'code',
  patch: 'code',
  requirement: 'requirement',
  plan: 'plan',
  'validation-report': 'validation',
  'context-kernel': 'file',
};

export function registryKindOf(type: string): WorkspaceArtifact['kind'] {
  return KIND_BY_TYPE[type] ?? 'file';
}

export function registryStatusOf(artifact: RegistryArtifact): StageStatus {
  switch (artifact.validationStatus) {
    case 'passed':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'pending':
      return 'running';
    case 'skipped':
      return 'skipped';
    default:
      return artifact.status === 'ready' ? 'passed' : 'pending';
  }
}

function metadataString(artifact: RegistryArtifact, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = artifact.metadata?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

/**
 * Group registry artifacts into version chains.
 *
 * A chain is keyed by its root id. An artifact whose parent is absent from the
 * input (filtered out, or from another session) starts its own chain rather
 * than being dropped — the registry is the source of truth and nothing it
 * returns should silently disappear from the workspace.
 */
export function buildVersionChains(artifacts: RegistryArtifact[]): ArtifactVersionChain[] {
  if (artifacts.length === 0) return [];

  const byId = new Map(artifacts.map(a => [a.id, a]));

  const rootIdOf = (artifact: RegistryArtifact): string => {
    const seen = new Set<string>();
    let current = artifact;
    while (current.parentArtifactId) {
      if (seen.has(current.id)) break; // defensive: cycle in stored data
      seen.add(current.id);
      const parent = byId.get(current.parentArtifactId);
      if (!parent) break;
      current = parent;
    }
    return current.id;
  };

  const groups = new Map<string, RegistryArtifact[]>();
  for (const artifact of artifacts) {
    const rootId = rootIdOf(artifact);
    const group = groups.get(rootId);
    if (group) group.push(artifact);
    else groups.set(rootId, [artifact]);
  }

  const chains: ArtifactVersionChain[] = [];
  for (const [rootId, group] of groups) {
    const versions = [...group].sort((a, b) => (
      a.version !== b.version ? a.version - b.version : a.createdAt - b.createdAt
    ));
    const root = byId.get(rootId) ?? versions[0];
    const active = [...versions].reverse().find(version => version.metadata?.isActiveVersion === true)
      || [...versions].reverse().find(version => version.status !== 'rejected')
      || root;
    chains.push({ root, versions, latest: versions[versions.length - 1], active });
  }

  // Newest activity first, so the most recently touched artifact leads the list.
  return chains.sort((a, b) => b.latest.createdAt - a.latest.createdAt);
}

/**
 * Adapt registry chains into the `WorkspaceArtifact` shape the workspace
 * already renders, so the registry replaces the message-derived list rather
 * than becoming a second parallel source.
 */
export function chainsToWorkspaceArtifacts(chains: ArtifactVersionChain[]): WorkspaceArtifact[] {
  return chains.map(chain => {
    const artifact = chain.active || chain.latest;
    return {
      id: artifact.id,
      kind: registryKindOf(artifact.type),
      title: artifact.name,
      status: registryStatusOf(artifact),
      version: artifact.version,
      codeVersionLabel: String(artifact.metadata?.codeVersionLabel || `v${artifact.version}`),
      isActive: true,
      sourceMessageId: metadataString(artifact, 'sourceMessageId', 'messageId', 'source_message_id'),
      createdAt: artifact.createdAt,
      origin: 'registry',
      fileName: metadataString(artifact, 'fileName', 'filename') || artifact.name,
      content: artifact.content,
      downloadable: Boolean(artifact.content),
      previewTarget: artifact.type === 'generated-code' || artifact.type === 'patch'
        ? 'code'
        : artifact.contentType.includes('markdown') ? 'markdown' : undefined,
    };
  });
}
